/**
 * Referral Notes API — append-only progress log for each referral card.
 *
 * The third peer of fileNotesApi.js and prepNotesApi.js: same table shape
 * (migration 28 mirrors prep_request_notes), same 200-character body cap,
 * same system / human note split, never edited or deleted (FAIS audit-trail
 * requirement). The only structural differences are the table
 * (referral_notes) and the parent column (referral_id).
 *
 * ACKNOWLEDGEMENT IS DELIBERATELY OFF. Referrals are worked by the Adviser
 * alone, so there is no counterpart role to acknowledge a note. The
 * acknowledgement columns exist for uniformity with the other two tables,
 * but every note is written with requires_ack = false and
 * acknowledger_role = null, nothing ever illuminates a referral card, and
 * there is no acknowledge RPC for this table.
 */

import supabase from './supabaseClient';

export const NOTE_BODY_MAX = 200;

function mapRowToNote(row) {
  return {
    id: row.id,
    referralId: row.referral_id,
    authorType: row.author_type,
    authorId: row.author_id || null,
    authorDisplayName: row.author_display_name || '',
    body: row.body || '',
    createdAt: row.created_at,
    // Always false / null for referral notes (see header) — mapped so the
    // shared NotesLog engine sees the same note shape as the other logs.
    requiresAck: row.requires_ack === true,
    acknowledgerRole: row.acknowledger_role || null,
    acknowledgedAt: row.acknowledged_at || null,
    acknowledgedBy: row.acknowledged_by || null,
  };
}

/**
 * Fetch all notes for a single referral, oldest first (journal order).
 */
export async function getNotesForReferral(referralId) {
  const { data, error } = await supabase
    .from('referral_notes')
    .select('*')
    .eq('referral_id', referralId)
    .order('created_at', { ascending: true });

  if (error) throw new Error(`Failed to fetch referral notes: ${error.message}`);
  return (data || []).map(mapRowToNote);
}

/**
 * Fetch note counts for many referrals in one query. Returns
 * { counts, pendingByRole } to match getNoteCountsByFile /
 * getNoteCountsByPrep, so the board can treat all three the same way;
 * pendingByRole is always empty here because referral notes never require
 * acknowledgement. This is a DISPLAY count for the card badge, not a
 * statistic (CLAUDE.md §21.1).
 */
export async function getNoteCountsByReferral() {
  const { data, error } = await supabase
    .from('referral_notes')
    .select('referral_id');

  if (error) throw new Error(`Failed to fetch referral note counts: ${error.message}`);
  const counts = {};
  for (const row of data || []) {
    counts[row.referral_id] = (counts[row.referral_id] || 0) + 1;
  }
  return { counts, pendingByRole: {} };
}

/**
 * Insert a new note. Body is trimmed and length-checked client-side (the DB
 * also enforces the 200-char ceiling — migration 28). `authorRole` is
 * accepted for adapter compatibility with the shared NotesLog contract but
 * never routes anything: requires_ack is always false.
 */
export async function createNote({
  referralId,
  authorType,
  authorId = null,
  authorDisplayName,
  body,
  authorRole = null, // eslint-disable-line no-unused-vars
}) {
  const trimmed = (body || '').trim();
  if (!trimmed) throw new Error('Note body is empty.');
  if (trimmed.length > NOTE_BODY_MAX) {
    throw new Error(`Note body exceeds ${NOTE_BODY_MAX} characters.`);
  }
  if (!authorType || (authorType !== 'user' && authorType !== 'system')) {
    throw new Error('authorType must be "user" or "system".');
  }
  if (!authorDisplayName) {
    throw new Error('authorDisplayName is required.');
  }

  const { data, error } = await supabase
    .from('referral_notes')
    .insert({
      referral_id: referralId,
      author_type: authorType,
      author_id: authorId,
      author_display_name: authorDisplayName,
      body: trimmed,
      requires_ack: false,
      acknowledger_role: null,
    })
    .select()
    .single();

  if (error) throw new Error(`Failed to create referral note: ${error.message}`);
  return mapRowToNote(data);
}

/**
 * Convenience helper: insert a system-authored note.
 */
export async function createSystemNote(referralId, body) {
  return createNote({
    referralId,
    authorType: 'system',
    authorId: null,
    authorDisplayName: 'System',
    body,
  });
}

/**
 * Present only so a NotesLog adapter over this store satisfies the shared
 * contract. It can never be reached in practice — the engine renders the
 * "Mark as read" control only for notes with requires_ack = true, and no
 * referral note is ever written that way — so it refuses loudly rather than
 * pretending to acknowledge.
 */
export async function acknowledgeNote() {
  throw new Error('Referral notes do not use acknowledgement.');
}
